const PRODUCT_TZ = "America/Sao_Paulo" as const;
const dayKeyFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: PRODUCT_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Chave "YYYY-MM-DD" no fuso BR para a data informada (default: agora). */
export function dayKeyForBR(d: Date = new Date()): string {
  return dayKeyFormatter.format(d);
}

/** Chave do dia anterior ao informado, no fuso BR. */
export function previousDayKeyBR(d: Date = new Date()): string {
  const ms = d.getTime() - 24 * 60 * 60 * 1000;
  return dayKeyForBR(new Date(ms));
}

/**
 * Semana ISO em UTC, "AAAA-Wnn" — espelho de `functions/src/shared/time.ts`
 * (`isoWeekKey`), paridade travada em `questCatalogParity.test.ts`. É a chave
 * do ledger da missão da semana (`user_stats/{uid}/weekly_quests/{weekKey}`)
 * e do escudo (`users.lastQuestShieldWeek`). ⚠️ UTC de propósito, igual ao
 * servidor: a semana vira às 21h de domingo em Brasília (00:00 UTC de
 * segunda), junto da virada da liga.
 */
export function isoWeekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

/**
 * Status da streak para um usuário a partir do `lastReadDate` salvo.
 * Espelha o cálculo do servidor sem refazer mutações.
 */
export interface StreakStatus {
  /** Usuário leu hoje (em BR) — fonte de verdade visual da chama. */
  isActiveToday: boolean;
  /** Tem streak ativa porém não leu hoje E ontem foi o último dia → vai perder. */
  isAtRisk: boolean;
  /**
   * Streak que pode ser EXIBIDA. Ver `computeEffectiveStreak` — é sempre este
   * valor que vai pra tela, nunca o `currentStreak` cru do documento.
   */
  effectiveStreak: number;
}

/**
 * Streak efetiva = a armazenada SE a última atividade cai dentro da janela
 * viva (hoje ou ontem em BR); caso contrário 0.
 *
 * Por que isto existe (BUG-47): `user_stats.currentStreak` só é zerado pelo
 * cron `dailyStreakReset`, que roda às 05:00 BR. Entre 00:00 e 05:00 a streak
 * JÁ MORREU (o último dia lido passou a ser anteontem) mas o documento ainda
 * carrega o valor antigo — e o servidor não se importa, porque
 * `calculateNextStreakBR` decide tudo por `lastReadDate`, nunca pelo contador.
 * Quem lia o contador cru era só a UI, que por isso exibia uma sequência morta
 * por até 5 horas. Este helper é o cinto que dispensa depender do suspensório:
 * mesmo que o cron atrase, falhe ou seja despausado, a tela nunca promete uma
 * ofensiva que o próximo registro de página vai recomeçar em 1.
 *
 * `lastReadDate` ausente com streak > 0 é documento inconsistente — tratamos
 * como não-verificável e exibimos 0 (conservador: nunca inventa sequência).
 * Não há risco de piscar durante o load porque `lastReadDate` e `currentStreak`
 * vêm do MESMO documento (`user_stats`), então chegam juntos.
 */
export function computeEffectiveStreak(
  lastReadDate: string | null | undefined,
  storedStreak: number,
  now: Date = new Date(),
): number {
  if (!(storedStreak > 0)) return 0;
  if (!lastReadDate) return 0;
  if (lastReadDate === dayKeyForBR(now)) return storedStreak;
  if (lastReadDate === previousDayKeyBR(now)) return storedStreak;
  return 0;
}

export function computeStreakStatus(
  lastReadDate: string | null | undefined,
  currentStreak: number,
  now: Date = new Date(),
): StreakStatus {
  const today = dayKeyForBR(now);
  const yesterday = previousDayKeyBR(now);
  const isActiveToday = lastReadDate === today;
  const isAtRisk = currentStreak > 0 && !isActiveToday && lastReadDate === yesterday;
  return {
    isActiveToday,
    isAtRisk,
    effectiveStreak: computeEffectiveStreak(lastReadDate, currentStreak, now),
  };
}
